import { useCallback, useRef, useState } from "react";
import type { MapRef } from "react-map-gl/maplibre";

export interface NetworkStats {
  totalLoadTime: number;
  tilesLoaded: number;
  measuring: boolean;
  estViewportLoad: number;
}

const EMPTY: NetworkStats = {
  totalLoadTime: 0,
  tilesLoaded: 0,
  measuring: false,
  estViewportLoad: 0,
};

/**
 * Measures map load performance by timing a full style reload and counting
 * tile load events from MapLibre. Works around the limitation that MapLibre
 * fetches tiles in web workers (invisible to main-thread fetch/Performance API).
 */
export function useNetworkStats(mapRef: React.RefObject<MapRef | null>) {
  const [stats, setStats] = useState<NetworkStats>(EMPTY);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const measure = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    let tileCount = 0;
    const start = performance.now();

    setStats({ ...EMPTY, measuring: true });
    clearTimeout(timer.current);

    // Count tile data events
    const onData = (e: { dataType?: string; tile?: unknown; sourceDataType?: string }) => {
      if (e.dataType === "source" && e.sourceDataType === "content") {
        tileCount++;
      }
    };

    // Detect when map becomes idle (all tiles loaded)
    let idleTimer: ReturnType<typeof setTimeout>;
    const onIdle = () => {
      // Debounce — map fires idle, then may load more tiles
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        finish();
      }, 1500);
    };

    const finish = () => {
      const elapsed = performance.now() - start;
      map.off("data", onData);
      map.off("idle", onIdle);
      clearTimeout(timer.current);
      clearTimeout(idleTimer);

      setStats({
        totalLoadTime: Math.round(elapsed),
        tilesLoaded: tileCount,
        measuring: false,
        estViewportLoad: Math.round(elapsed),
      });
    };

    map.on("data", onData);
    map.on("idle", onIdle);

    // Force full style reload — clears tile cache, re-fetches everything
    const currentStyle = map.getStyle();
    if (currentStyle) {
      map.setStyle(currentStyle, { diff: false });
    }

    // Hard timeout: stop after 15 seconds regardless
    timer.current = setTimeout(() => {
      finish();
    }, 15000);
  }, [mapRef]);

  return { stats, measure };
}
